import React, { useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import CustomizeIcon from '@atlaskit/icon/core/customize';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Text, xcss } from '@atlaskit/primitives';

import {
	type PrimaryProductKey,
	PrimaryProductKeys,
	ProductKeys,
	useProductConfigs,
} from '../../../../../common/constants/products';
import { PRODUCTS_WITH_INLINE_FILTERS } from '../../../../../common/constants/quick-find';
import { SearchExperience } from '../../../../../common/constants/search';
import { FilterButton } from '../../../../../common/ui/quick-find/filter-button';
import {
	serveQFAppSelectorExperiment,
	serveQFDropdownExperiment,
} from '../../../../../common/utils/experiments';
import {
	useQuickFindAnalytics,
	useQuickFindAttributes,
} from '../../../../../controllers/quick-find/analytics';
import {
	useAnyFilterSelected,
	useFiltersDiscoverability2Enabled,
	useFiltersToDisplay,
	usePrimaryProduct,
	useSelectedProduct,
} from '../../../../../controllers/store';
import { useSearchActions } from '../../../../../controllers/store/search';
import {
	assetsSchemaFilter,
	assetsTypeFilter,
	assigneeFilter,
	commentedByMeFilter,
	contributorFilter,
	projectFilter,
	spaceFilter,
	watchedFilter,
} from '../../../../../controllers/store/search/filters';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { filters } from '../../../../search-page/filters/product-filters';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import DropdownFilter from '../../../../search-page/filters/product-filters/dropdown';
import {
	AssetsSchemaFilter,
	AssetsTypeFilter,
	AssigneeFilter,
	ContributorFilter,
	ProjectFilter,
	SpaceFilter,
	// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
} from '../../../filters/product-filters';

import { AppSelector } from './app-selector';
import { i18n } from './messages';
import { useFilterAnalytics, useUnselectProductFilter } from './utils';

const oldInlineProductFilterStyles = xcss({
	display: 'flex',
	paddingInlineStart: 'space.100',
	gap: 'space.100',
	width: '100%',
	minWidth: '0', // Prevents the filter buttons from overflowing from its container and allow it to shrink as much as possible
});

const newInlineProductFilterStyles = xcss({
	display: 'flex',
	gap: 'space.100',
	minWidth: '0', // Prevents the filter buttons from overflowing from its container and allow it to shrink as much as possible
});

const appSelectorDividerStyles = xcss({
	borderWidth: '1px',
	borderColor: 'color.border',
	borderRightStyle: 'solid',
	marginBlock: 'space.025',
});

type InlineProductFiltersProps = {
	onAllFiltersClickCallback?: (params: {
		action?: 'filterByPrimaryProduct';
		event: React.MouseEvent;
	}) => void;
};

const prioritisedFilters: Record<PrimaryProductKey, string[]> = {
	[PrimaryProductKeys.Confluence]: [spaceFilter.id, contributorFilter.id],
	[PrimaryProductKeys.Jira]: [projectFilter.id, assigneeFilter.id],
	// Not supported
	[PrimaryProductKeys.Atlas]: [],
	[PrimaryProductKeys.Focus]: [],
	[PrimaryProductKeys.Assets]: [assetsSchemaFilter.id, assetsTypeFilter.id],
	[PrimaryProductKeys.Elevate]: [],
	[PrimaryProductKeys.Talent]: [],
	[PrimaryProductKeys.RovoExtension]: [],
};

const ClearAllButton = ({ onClear }: { onClear: VoidFunction }) => {
	const { formatMessage } = useIntl();
	return (
		<Box id="clear-all-button">
			<Button onClick={onClear} appearance="subtle">
				<Text>{formatMessage(i18n.clearFilters)}</Text>
			</Button>
		</Box>
	);
};

export const InlineProductFilters = ({
	onAllFiltersClickCallback,
}: InlineProductFiltersProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [primaryProduct] = usePrimaryProduct();
	const [selectedProduct] = useSelectedProduct();
	const productConfigs = useProductConfigs();
	const [isAnyFilterSelected] = useAnyFilterSelected();
	const filtersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const { clearAllFilters, resetSort } = useSearchActions();
	const [{ filtersInLine }] = useFiltersToDisplay({
		basedOnPrimaryProduct: true,
		primaryProduct,
		prioritisedFilterIds: primaryProduct ? prioritisedFilters[primaryProduct] : [],
	});
	const filtersToRender = filtersInLine
		.filter((f) => f !== commentedByMeFilter.id && f !== watchedFilter.id)
		.slice(0, 2); // Only show 2 filters at a time

	const serveQFAppSelector = serveQFAppSelectorExperiment(primaryProduct, {
		fireExperimentExposure: false,
	});
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes } = useQuickFindAttributes();
	const { onFilterChange, onDropdownOpenChange, onFilterClearAction } = useFilterAnalytics(
		fireAnalyticsEvent,
		commonAttributes,
	);
	useUnselectProductFilter({ isEnabled: !serveQFAppSelector });

	const oldInlineProductFilters = (
		<Box xcss={oldInlineProductFilterStyles} testId="qf-product-filters">
			{/* TODO: Dynamically populate based on selected product. Hardcoded for Team25 */}
			<SpaceFilter />
			<FilterButton
				onClick={(event) => {
					onAllFiltersClickCallback?.({
						...(filtersDiscoverability2Enabled && {
							filterDropdownOpen: true,
						}),
						event,
					});
				}}
				iconAfter={
					<LinkExternalIcon
						label={formatMessage(i18n.externalLinkIconLabel)}
						color="currentColor"
						size="small"
					/>
				}
			>
				{formatMessage(i18n.moreButtonLabel)}
			</FilterButton>
		</Box>
	);

	/**
	 * Defines inline product filters to show for each primary product. Given that
	 * the filters we decide to show are determined by product and design, there
	 * isn't a need to make this dynamic yet.
	 */
	const newInlineProductFilters = useMemo(() => {
		// rovo-extension uses Confluence's config since it's not a searchable product
		const productConfigKey =
			primaryProduct === PrimaryProductKeys.RovoExtension && fg('quickfind-rovo-extension')
				? ProductKeys.Confluence
				: (primaryProduct as ProductKeys);
		const primaryProductDisplayName = productConfigs[productConfigKey]?.displayName ?? '';
		const moreFiltersButton = (
			<FilterButton
				onClick={(event) => {
					onAllFiltersClickCallback?.({
						...(filtersDiscoverability2Enabled && {
							filterDropdownOpen: true,
						}),
						action: 'filterByPrimaryProduct',
						event,
					});
				}}
				iconAfter={<CustomizeIcon label={formatMessage(i18n.filterByIcon)} />}
				noBorder={true}
				tooltip={formatMessage(i18n.moreButtonTooltip, { product: primaryProductDisplayName })}
				testId="quick-find-inline-product-filters-more-filters-button"
			/>
		);

		if (
			(primaryProduct === PrimaryProductKeys.Confluence ||
				primaryProduct === PrimaryProductKeys.Jira ||
				(primaryProduct === PrimaryProductKeys.RovoExtension && fg('quickfind-rovo-extension'))) &&
			serveQFDropdownExperiment(primaryProduct, { fireExperimentExposure: false })
		) {
			return (
				<Box xcss={newInlineProductFilterStyles} testId="qf-product-filters">
					<DropdownFilter
						hideOnHoverOut={
							primaryProduct === PrimaryProductKeys.Jira &&
							fg('search_qf_close_dropdown_on_hover_off')
						}
						experience={SearchExperience.QUICK_FIND}
						onFiltersChange={onFilterChange}
						onDropdownOpenChange={onDropdownOpenChange}
					/>
					{serveQFAppSelector && (
						<>
							<AppSelector fit="compact" />
							<Box xcss={appSelectorDividerStyles} />
						</>
					)}
					{filtersToRender.map((id) => {
						const Filter = filters[id as keyof typeof filters];
						if (!Filter) {
							return null;
						}
						return (
							<Filter
								key={id}
								showPrimaryProductIcon
								fit="compact"
								hideOnHoverOut={
									primaryProduct === PrimaryProductKeys.Jira &&
									fg('search_qf_close_dropdown_on_hover_off')
								}
								onFilterChange={(change) => onFilterChange(id, change, 'inline')}
							/>
						);
					})}
					{isAnyFilterSelected && (
						<ClearAllButton
							onClear={() => {
								clearAllFilters();
								resetSort();
								onFilterClearAction('inline');
							}}
						/>
					)}
				</Box>
			);
		}
		// Delete the else if statements here when cleaning up serveQFDropdownExperiment
		else if (
			primaryProduct === PrimaryProductKeys.Confluence ||
			(primaryProduct === PrimaryProductKeys.RovoExtension && fg('quickfind-rovo-extension'))
		) {
			return (
				<Box xcss={newInlineProductFilterStyles} testId="qf-product-filters">
					{serveQFAppSelector && <AppSelector />}
					<SpaceFilter />
					{filtersDiscoverability2Enabled && <ContributorFilter />}
					{moreFiltersButton}
				</Box>
			);
		} else if (primaryProduct === PrimaryProductKeys.Jira) {
			return (
				<Box xcss={newInlineProductFilterStyles} testId="qf-product-filters">
					{serveQFAppSelector && <AppSelector />}
					<ProjectFilter />
					<AssigneeFilter />
					{moreFiltersButton}
				</Box>
			);
		} else if (primaryProduct === PrimaryProductKeys.Assets && fg('assets_rovo_search_all')) {
			return (
				<Box xcss={newInlineProductFilterStyles} testId="qf-product-filters">
					<AssetsSchemaFilter />
					<AssetsTypeFilter />
					{moreFiltersButton}
				</Box>
			);
		} else {
			return null; // Keep this when cleaning up serveQFDropdownExperiment
		}
	}, [
		clearAllFilters,
		filtersDiscoverability2Enabled,
		filtersToRender,
		formatMessage,
		isAnyFilterSelected,
		onAllFiltersClickCallback,
		onDropdownOpenChange,
		onFilterChange,
		onFilterClearAction,
		primaryProduct,
		productConfigs,
		resetSort,
		serveQFAppSelector,
	]);

	if (PRODUCTS_WITH_INLINE_FILTERS.includes(primaryProduct)) {
		return newInlineProductFilters;
	}

	if (selectedProduct) {
		return oldInlineProductFilters;
	}

	return null;
};
